import { AiDocumentSource, AiSource } from "components/ai_chat/types";
import { LeadDetails } from "lib/hooks/api/leads/useLead";
import { AiSourceType, inferEnumType } from "lib/types/graphQLEnums";

type LeadDescriptionSource = NonNullable<LeadDetails["descriptionSources"]>[number];

/**
 * Converts a lead's `descriptionSources` (the shared `aiSource` GraphQL shape) into `AiSource[]`
 * for `AnswerMarkdown` inline-citation rendering.
 *
 * Deliberately NOT reusing the bid `toInlineSources()`: the lead and bid source fragments compile
 * to distinct generated enum types (`type`/`documentType`), so sharing the bid-typed function would
 * require an unsafe cast that hides the seam and breaks silently on drift. This mirrors its logic
 * over the lead's own shape instead.
 */
export function toLeadInlineSources(sources: LeadDescriptionSource[]): AiSource[] {
  return sources.flatMap((s, i): AiSource[] => {
    // The backend always sets `type` for lead sources; `citationId` falls back to position.
    const citationId = s.citationId ?? String(i + 1);

    if (s.type === "notice") {
      return [
        {
          type: AiSourceType.Notice,
          id: s.id ?? "",
          title: s.title ?? "Notice",
          citationId,
        },
      ];
    }

    if (s.type === "website" && s.url) {
      return [{ type: AiSourceType.Website, title: s.title ?? "", url: s.url, citationId }];
    }

    if (s.type === "document" || s.id) {
      return [
        {
          type: AiSourceType.Document,
          id: s.id ?? "",
          title: s.title ?? "",
          citationId,
          documentType: inferEnumType(s.documentType),
          chunkNumber: s.chunkNumber ?? 0,
          locations: (s.locations ?? []) as AiDocumentSource["locations"],
          isDeleted: s.isDeleted ?? false,
        },
      ];
    }

    return [];
  });
}
